import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, User, Wallet, ArrowLeftRight, BarChart2,
  ArrowDownToLine, ShoppingBag, Code2, Globe, Headphones,
  ChevronLeft, ChevronRight, Package, Users,
  Settings, LineChart, Bell, Webhook, SlidersHorizontal, FileCheck, Link2, LayoutGrid, ShieldCheck,
  Briefcase, MessageSquare, MessageCircle,
} from 'lucide-react';

/* ── Admin nav groups ── */
const adminNav = [
  {
    group: 'Overview',
    items: [
      { path: '/admin/dashboard',          icon: LayoutDashboard,   label: 'Dashboard',           tabKey: 'dashboard'     },
    ],
  },
  {
    group: 'Merchants',
    items: [
      { path: '/admin/merchants',          icon: Users,             label: 'Merchants',           tabKey: 'merchants'     },
      { path: '/admin/users',              icon: User,              label: 'All Users',           tabKey: 'users'         },
      { path: '/admin/kyc',                icon: FileCheck,         label: 'KYC Forms',           tabKey: 'kyc'           },
      { path: '/admin/business-partners',  icon: Briefcase,         label: 'Business Partners',   tabKey: 'business_partners' },
      { path: '/admin/campaign/sms',       icon: MessageSquare,     label: 'SMS Campaign',        tabKey: 'sms_campaign'      },
      { path: '/admin/campaign/whatsapp',  icon: MessageCircle,     label: 'WhatsApp Campaign',   tabKey: 'whatsapp_campaign' },
    ],
  },
  {
    group: 'Payments',
    items: [
      { path: '/admin/transactions',       icon: ArrowLeftRight,    label: 'Transactions',        tabKey: 'transactions'  },
      { path: '/admin/settlements',        icon: BarChart2,         label: 'Settlements',         tabKey: 'settlements'   },
      { path: '/admin/webhook-logs',       icon: Bell,              label: 'Webhook Logs',        tabKey: 'webhook_logs'  },
      { path: '/admin/gateway-manage',     icon: SlidersHorizontal, label: 'Gateway Manage',      tabKey: 'gateway_manage' },
    ],
  },
  {
    group: 'Settings',
    items: [
      { path: '/admin/support',            icon: Headphones,        label: 'Support Tickets',     tabKey: 'support'       },
      { path: '/admin/commission',         icon: LineChart,         label: 'Commission',          tabKey: 'commission'    },
      { path: '/admin/site-settings',      icon: Settings,          label: 'Site Settings',       tabKey: 'site_settings' },
      { path: '/admin/merchant-tabs',      icon: LayoutGrid,        label: 'Merchant Tabs',       tabKey: 'merchant_tabs' },
      { path: '/admin/roles',              icon: ShieldCheck,       label: 'Roles & Permissions', tabKey: 'roles'         },
    ],
  },
];

/* ── Business Partner nav groups ── */
const bpNav = [
  {
    group: 'Overview',
    items: [
      { path: '/bp/dashboard',    icon: LayoutDashboard, label: 'Dashboard',    bpKey: 'dashboard'    },
    ],
  },
  {
    group: 'My Business',
    items: [
      { path: '/bp/merchants',    icon: Users,           label: 'My Merchants', bpKey: 'merchants'    },
      { path: '/bp/kyc-links',    icon: FileCheck,       label: 'KYC Links',    bpKey: 'kyc_links'    },
      { path: '/bp/transactions', icon: ArrowLeftRight,  label: 'Transactions', bpKey: 'transactions' },
      { path: '/bp/settlements',  icon: LineChart,       label: 'Settlements',  bpKey: 'settlements'  },
    ],
  },
];

/* ── User nav groups ── */
const userNav = [
  {
    group: 'Overview',
    items: [
      { path: '/user/dashboard',    icon: LayoutDashboard, label: 'Dashboard' },
    ],
  },
  {
    group: 'Account',
    items: [
      { path: '/user/profile',      icon: User,            label: 'Profile',       tabKey: 'profile'       },
      { path: '/user/wallet',       icon: Wallet,          label: 'Wallet',        tabKey: 'wallet'        },
    ],
  },
  {
    group: 'Payments',
    items: [
      { path: '/user/transactions', icon: ArrowLeftRight,  label: 'Transactions',  tabKey: 'transactions'  },
      { path: '/user/settlements',  icon: BarChart2,       label: 'Settlements',   tabKey: 'settlements'   },
      { path: '/user/catalogue',    icon: Package,         label: 'Catalogue',     tabKey: 'catalogue'     },
      { path: '/user/payment-links',icon: Link2,           label: 'Payment Links', tabKey: 'payment_links' },
    ],
  },
  {
    group: 'Integrations',
    items: [
      { path: '/user/shopify',      icon: ShoppingBag,     label: 'Shopify Setup', tabKey: 'shopify'       },
      { path: '/user/integration',  icon: Code2,           label: 'API Setup',     tabKey: 'integration'   },
      { path: '/user/wordpress',    icon: Globe,           label: 'WordPress',     tabKey: 'wordpress'     },
    ],
  },
  {
    group: 'Help',
    items: [
      { path: '/user/support',      icon: Headphones,      label: 'Support',       tabKey: 'support'       },
    ],
  },
];

const PanelSidebar = ({ role, user, collapsed, toggleCollapsed, mobileOpen, closeMobile }) => {
  const location    = useLocation();
  const disabledTabs = user?.disabled_tabs || [];

  const canSeeAdminTab = (tabKey) => {
    if (!tabKey) return true;
    if (user?.is_super_admin) return true;
    const perms = user?.permissions;
    if (!perms) return true;
    return !!(perms[tabKey]?.read || perms[tabKey]?.write);
  };

  const canSeeBPTab = (bpKey) => {
    if (!bpKey) return true;
    const perms = user?.partner_permissions;
    if (!perms) return true;
    return !!(perms[bpKey]?.read || perms[bpKey]?.write);
  };

  let baseGroups;
  if (role === 'business_partner') {
    baseGroups = bpNav;
  } else if (role === 'admin') {
    baseGroups = adminNav;
  } else {
    baseGroups = userNav;
  }

  const groups = role === 'admin'
    ? baseGroups
        .map(g => ({ ...g, items: g.items.filter(it => canSeeAdminTab(it.tabKey)) }))
        .filter(g => g.items.length > 0)
    : role === 'business_partner'
    ? baseGroups
        .map(g => ({ ...g, items: g.items.filter(it => canSeeBPTab(it.bpKey)) }))
        .filter(g => g.items.length > 0)
    : baseGroups
        .map(g => ({ ...g, items: g.items.filter(it => !it.tabKey || !disabledTabs.includes(it.tabKey)) }))
        .filter(g => g.items.length > 0);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <aside className={`panel-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'open' : ''}`}>

      {/* ── Brand ── */}
      <div className="panel-sidebar__brand">
        <div className="panel-sidebar__brand-logo">A1</div>
        <div className="panel-sidebar__brand-name">
          A1<b>X</b>PAY
        </div>
      </div>

      {/* ── Nav ── */}
      <nav className="panel-sidebar__nav">
        {groups.map((g, gi) => (
          <div key={gi} className="panel-sidebar__group">
            <div className="panel-sidebar__group-label">{g.group}</div>
            {g.items.map((it) => {
              const Icon   = it.icon;
              const active = isActive(it.path);
              return (
                <Link
                  key={it.path}
                  to={it.soon ? '#' : it.path}
                  onClick={it.soon ? (e) => e.preventDefault() : closeMobile}
                  className={`panel-sidebar__link ${active ? 'active' : ''}`}
                  title={it.label}
                  style={it.soon ? { opacity: 0.45, cursor: 'default', pointerEvents: 'none' } : {}}
                >
                  <span className="panel-sidebar__link-icon">
                    <Icon size={15} strokeWidth={1.8} />
                  </span>
                  <span className="panel-sidebar__link-label">
                    {it.label}
                    {it.soon && (
                      <span style={{
                        marginLeft: 6, fontSize: 9, fontWeight: 700,
                        background: '#ede9fe', color: '#7c3aed',
                        borderRadius: 4, padding: '1px 5px',
                        letterSpacing: .3, textTransform: 'uppercase',
                      }}>
                        Soon
                      </span>
                    )}
                  </span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* ── Footer — version + collapse toggle ── */}
      <div className="panel-sidebar__footer">
        <span className="panel-sidebar__version">v2.0</span>
        <button
          className="panel-sidebar__collapse"
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
        </button>
      </div>
    </aside>
  );
};

export default PanelSidebar;
